/* eslint-disable max-len */
import { AuthenticatedBank } from '~/entities/responses/authenticatedBank';
import { GetAccountOBDXResponse } from '~/entities/responses/getAccountOBDXResponse';
import { getPaymentBankResponse } from '~/entities/responses/getPaymentBankResponse';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { GetPostInitBankTransfertResponse } from '~/entities/responses/getPostInitBankTransfertResponse';
import { GetPostPaymentMobileResponse } from '~/entities/responses/getPostPaymentMobileResponse';
import { GetPostPaymentResponse } from '~/entities/responses/getPostPaymentResponse';
import { GetPublicKeyResponse } from '~/entities/responses/getPublicKeyResponse';
import { GetTransactionResponse } from '~/entities/responses/getTransactionResponse';
import { GetValidatePaymentResponse } from '~/entities/responses/getValidatePaymentResponse';
import { AvailableBank } from '~/entities/schemas/availableBank';
import { AvailableCountry } from '~/entities/schemas/availableCountry';
import { GimacCountry } from '~/entities/schemas/gimacCountry';
import { logger } from '~/utils/func/logger';

import { APIService } from './ApiService';
import { CryptoService } from './CryptoService';

export interface ProcessPaymentBody {
  transactionRef: string;
  channelId: string;
  paymentMethod?: string;
  PaymentMethod?: string;
  cardInfos?: CardInfos;
  mobileMoneyInfos?: MobileMoneyInfos | null;
}

interface ProcessMobilePaymentBody {
  transactionRef: string;
  channelId: string;
  paymentMethod?: string;
  mobileMoneyInfos?: MobileMoneyInfos;
}

export interface CardInfos {
  encryptedCard: string;
}

interface MobileMoneyInfos {
  PayItemId?: string;
  phoneNumber: string;
  network?: string;
  currency?: string;
  country?: string;
  PhoneNumber?: string;
}

export interface FlutterwaveCardInfos {
  card_number: string;
  cvv: string;
  card: string;
  expiry_month: string;
  expiry_year: string;
  currency: string;
  amount: string;
  email: string;
  fullname: string;
  tx_ref: string;
  phone_number: string;
  redirect_url: string;
  paymentMethod?: string;
}

export interface PinBody extends FlutterwaveCardInfos {
  authorization: PinAuthorization;
}

export interface PinAuthorization {
  mode: string;
  pin: string;
}

export interface OtpAuthorization {
  mode: string;
  otp: string;
}

export interface OtpBody extends FlutterwaveCardInfos {
  authorization: OtpAuthorization;
}

export interface FinalValidateBody {
  otp: string;
  transactionRef: string;
}

export interface AvsAuthorization extends AvsAddressbody {
  mode: string;
}

export interface AvsBody extends FlutterwaveCardInfos {
  authorization: AvsAuthorization;
}

export interface AvsAddressbody {
  city: string;
  address: string;
  state: string;
  country: string;
  zipcode: string;
}

export interface AppleGooglePayBody {
  transactionRef: string;
  channelId: string;
  paymentMethod: 'ApplePay' | 'GooglePay';
}

export type AppleGooglePayType = 'apple' | 'google';

export interface InitVerifyUserOBDXBody {
  username: string;
  password: string;
  countryCode: string;
}

export interface InitBankTransferBody {
  transactionRef: string;
  channelId: string;
  paymentMethod: string;
  bankTransfert: {
    applicationCode: string;
    countryCode: string;
    productCode: string;
    currencyCode: string;
    upiCode: string;
    bankAccount: string;
    token: string;
    initiator: {
      dateOfBirth: string;
    };
  };
}

export interface PaymentBankTransferBody {
  transactionRef: string;
  channelId: string;
  paymentMethod: string;
  bankTransfert: {
    applicationCode: string;
    countryCode: string;
    productCode: string;
    currencyCode: string;
    upiCode: string;
    bankAccount: string;
    token: string;
    initiator: {
      OTP: string;
    };
  };
}

export interface GimacRequestToPayBody {
  intent: string;
  issuerTrxRef: string;
  walletSource: string;
  walletDestination: string;
  description: string;
  amount: number;
  currency: string;
  purchaseRef: string;
  toMember: string;
  validityDuration: number;
  createTime: number;
}

export const PaymentService = {
  async getPaymentDetails(token: string): Promise<GetPaymentResponse> {
    const res = await APIService.call('GET', `/payment/${token}`);
    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetPaymentResponse;

    localStorage.setItem('data', resData);
    logger.debug('decryptData decryptData', { prefix: 'PaymentService', data: decryptData });
    return decryptData;
  },
  async getPublicKey(): Promise<GetPublicKeyResponse> {
    const res = await APIService.call('GET', `/flocash/token`);
    return res.data;
  },
  async processPayment(body: ProcessPaymentBody): Promise<GetPostPaymentResponse> {
    const res = await APIService.call('POST', `/payment/pay`, body, undefined, undefined, undefined, undefined, true);
    const resData = res.data.data;
    logger.debug('resData test', { prefix: 'PaymentService', data: resData });

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetPostPaymentResponse;
    logger.debug('decryptData test', { prefix: 'PaymentService', data: decryptData });
    return decryptData;
  },
  async processMobilePayment(body: ProcessMobilePaymentBody): Promise<GetPostPaymentMobileResponse> {
    const res = await APIService.call(
      'POST',
      `/payment/mobileMoneyFranco`,
      body,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    );
    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetPostPaymentMobileResponse;
    return decryptData;
  },
  async getTransactionDetails(transactionRef: string): Promise<GetTransactionResponse> {
    const res = await APIService.call('GET', `/payment/details`, undefined, `?TransactionId=${transactionRef}`);

    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetTransactionResponse;

    decryptData.data.transaction.transactionRef = transactionRef;
    localStorage.setItem('transactionDetails', resData);
    return decryptData;
  },
  async getTransactionReceipt(transactionRef: string): Promise<string> {
    const res = await APIService.call(
      'GET',
      `/payment/receipt/${transactionRef}`,
      undefined,
      undefined,
      undefined,
      undefined,
      'blob',
    );
    const contentType = res.headers['content-type'];
    if (contentType && contentType.toLowerCase().includes('application/pdf')) {
      const fileData = new Blob([res.data], { type: contentType });
      const pdfURL = URL.createObjectURL(fileData);
      return pdfURL;
    } else {
      logger.error('La réponse ne contient pas un fichier PDF.', undefined, { prefix: 'PaymentService' });
      return '';
    }
  },
  async flutterwaveEncryptCard(
    body: FlutterwaveCardInfos | PinBody | AvsBody | OtpBody,
  ): Promise<GetPublicKeyResponse> {
    const res = await APIService.call('POST', `/flutterwave/token`, body);
    return res.data;
  },
  async validatePayment(body: ProcessPaymentBody): Promise<GetValidatePaymentResponse> {
    const res = await APIService.call(
      'POST',
      `/payment/pay/validate`,
      body,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    );

    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetValidatePaymentResponse;
    return decryptData;
  },
  async finalValidatePayment(body: FinalValidateBody): Promise<GetValidatePaymentResponse> {
    const res = await APIService.call('POST', `/payment/pay/final-validate`, body);
    return res.data;
  },
  async initAppleGooglePay(body: AppleGooglePayBody, paymentType: AppleGooglePayType): Promise<GetPostPaymentResponse> {
    const res = await APIService.call(
      'POST',
      `/payment/${paymentType}`,
      body,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    );
    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetPostPaymentResponse;

    return decryptData;
  },
  async getAvailableCountries(): Promise<AvailableCountry[]> {
    const res = await APIService.call('GET', '/flutterwave/availableCountryForMM');
    return res.data;
  },
  async getTransactionApp(transactionRef: string): Promise<GetTransactionResponse> {
    const res = await APIService.call('GET', `/payment/details`, undefined, `?TransactionId=${transactionRef}`);

    const resData = res.data.data;

    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(resData) as GetTransactionResponse;

    // decryptData.data.transaction.transactionRef = transactionRef;
    return decryptData;
  },
  async getAvailableBanks(): Promise<AvailableBank[]> {
    const res = await APIService.call('GET', `/payment/availableBankForBT`);
    return res.data;
  },

  async getTokenAuthenticatedBank(): Promise<AuthenticatedBank> {
    const res = await APIService.call('GET', `/payment/authenticate/banktransfert`);
    return res.data;
  },

  async processInitBankTransfer(body: InitBankTransferBody): Promise<GetPostInitBankTransfertResponse> {
    const res = await APIService.call(
      'POST',
      `/payment/initiate/banktransfert`,
      body,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    );
    return res.data;
  },

  async processPaymentBankTransfer(body: PaymentBankTransferBody): Promise<getPaymentBankResponse> {
    const res = await APIService.call('POST', '/payment/pay', body, undefined, undefined, undefined, undefined, true);
    const resData = res.data;
    return resData;
    // const crypto = new CryptoService();
    // crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    // const decryptData = crypto.decryptData(resData) as getPaymentBankResponse;
    // return decryptData;
  },

  async processVerificationUserOBDX(body: InitVerifyUserOBDXBody): Promise<GetAccountOBDXResponse> {
    const res = await APIService.call(
      'POST',
      `/payment/OBDXLogin`,
      body,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
    );
    return res.data;
  },

  async getGimacCountries(): Promise<GimacCountry[]> {
    const res = await APIService.call('GET', '/gimacpay/countries');
    return res.data;
  },

  async initGimacRequestToPay(body: GimacRequestToPayBody): Promise<any> {
    const res = await APIService.call('POST', '/gimacpay/requesttopay/init', body);
    return res.data;
  },
};
